// Copyright (c) 2025 TrustCloud Corporation - All rights reserved.
//
// This software contains valuable confidential and proprietary information of
// TrustCloud Corporation
//
// Unauthorized use, reproduction, transmission, or distribution of this file and its
// contents is a violation of applicable law.

import React from 'react';
import { Link, useCurrentRoute } from 'react-navi';
import { color, Tag, Typography } from '@kintent/glide';
import { format } from 'date-fns';
import { UilArrowLeft } from '@iconscout/react-unicons';
import { useTheme } from '@emotion/react';

import * as Styles from './NotificationDetails.styles';
import NotificationLeftPanel from '../notificationsv2/components/NotificationLeftPanel';
import { NOTIFICATION_PAGE_TITLE } from '../notificationsv2/lib/constants';
import { Helmet } from '../../../components/Helmet';
import { Flex } from '../../../components/Flex';
import { useTrustShareNotifications } from '../../../lib/state';
import { NOTIFICATION_TYPE_COLOR_VARIANTS, TRUSTSHARE_NOTIFICATION_TYPE_LABELS } from '../../../lib/constants';

function NotificationDetailPanel() {
  const {
    data: { notificationId },
  } = useCurrentRoute();
  const theme = useTheme();
  const { data: allNotifications } = useTrustShareNotifications();
  const notification = allNotifications?.find((item) => item.id === notificationId);
  if (notification == null) return null;

  const formattedPublishedDate =
    notification.publishedDate != null ? format(new Date(notification.publishedDate), 'EEEE, MMMM d, yyyy') : null;
  const { type } = notification;

  return (
    <Flex
      direction="column"
      gap="16px"
    >
      <Styles.NotificationListLinkContainer
        as={Link}
        gap="12px"
        href="/notifications"
        alignItems="center"
      >
        <UilArrowLeft
          size="1.2rem"
          fill={color(theme.colorV2.system.gray, 600)}
        />
        <Typography
          strength="60%"
          level="8"
        >
          Back to All Notifications
        </Typography>
      </Styles.NotificationListLinkContainer>
      <Styles.NotificationDetailsContainer>
        <Styles.NotificationDetailHeader>
          <Typography
            className="notification-detail-title"
            as="h4"
            level="4"
          >
            {notification.title}
          </Typography>
          <Styles.NotificationDetailsMetadata>
            <Tag
              type="category"
              variant={NOTIFICATION_TYPE_COLOR_VARIANTS[type]}
            >
              {TRUSTSHARE_NOTIFICATION_TYPE_LABELS[type]}
            </Tag>
            {formattedPublishedDate != null && (
              <Typography
                as="p"
                strength="60%"
                level="8"
                className="notification-detail-date"
              >
                {formattedPublishedDate}
              </Typography>
            )}
          </Styles.NotificationDetailsMetadata>
        </Styles.NotificationDetailHeader>
        <Typography
          as="p"
          strength="80%"
          level="8"
          dangerouslySetInnerHTML={{ __html: notification.description }}
          className="notification-detail-description"
        />
      </Styles.NotificationDetailsContainer>
    </Flex>
  );
}

function NotificationDetails() {
  return (
    <>
      <Helmet pageTitle="Notification Details" />
      <Styles.Wrapper data-testid="enterprise-main-notification-detail-layout">
        <Typography
          as="h1"
          level="1"
        >
          {NOTIFICATION_PAGE_TITLE}
        </Typography>
        <section className="notification-section-grid">
          <NotificationLeftPanel />
          <NotificationDetailPanel />
        </section>
      </Styles.Wrapper>
    </>
  );
}

export default NotificationDetails;
